import { useMemo } from 'react';

import { StoreItem } from '@givebacks/api/store';
import { useForm, useFormyValidator } from '@givebacks/formy';
import { RichTextDisplay } from 'rich-text';
import { AdditionalFieldOption, Select, Stack, Text } from 'ui';
import { UseFormReturnType, isNotEmpty } from 'ui/Form';

import {
  CheckoutButtons,
  CheckoutButtonsProps,
} from '../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons';
import { MembershipUnitForm, MembershipUnitFormType } from './MembershipUnitForm';

type MembershipFormsType = {
  membership_form: UseFormReturnType<MembershipUnitForms>;
};

export type MembershipUnitForms = {
  year?: number;
  units: MembershipUnitFormType[];
};

type MembershipUnitFormsProps = {
  /* Membership are allowed to be sold for the current school year.
   * If it is march & the organization allows memberships to be sold, then you can buy the next school year. Not all organization allow this.
   * To take the complexity away from this, the API provides the school years allowed to be bought.
   */
  allowedYears?: { label: string; value: number }[];
  options: AdditionalFieldOption[];
  // unit on a StoreItems::Membership record the number of memberships required for that membership item
  units: number;
  storeItem: StoreItem;
  storeId: string;
};

const MembershipUnitForms = ({
  allowedYears,
  options,
  units,
}: {
  allowedYears?: { label: string; value: number }[];
  options: AdditionalFieldOption[];
  units: number;
}) => {
  const _units = units ?? 1;
  //To track the options for each of our Membership unit forms, we will create a set of options based on the unit number that will then be added to each form
  const _options = useMemo(() => {
    const _optionsInitial: MembershipUnitFormType[] = [];
    for (let index = 0; index < _units; index++) {
      _optionsInitial.push({} as MembershipUnitFormType);
    }
    return _optionsInitial;
  }, [_units]);

  const form = useForm<MembershipUnitForms>(
    {
      initialValues: {
        year: undefined,
        units: _options,
      },
      validate: {
        // we don't care about options as AdditionalFields handles that
        year: isNotEmpty('Year is required'),
      },
      validateInputOnBlur: true,
    },
    { id: 'membership_form' }
  );

  return (
    <Stack gap="lg" ref={form.formRef}>
      {allowedYears && (
        <Select
          label="School Year"
          data={allowedYears.map((year) => ({ value: String(year.value), label: year.label }))}
          required
          {...form.getInputProps('year')}
        />
      )}
      {form.values.units.map((_, index) => (
        <MembershipUnitForm
          key={index}
          options={options}
          index={index}
          onChange={(index, changes) => form.setFieldValue(`units.${index}`, changes)}
        />
      ))}
    </Stack>
  );
};
interface MembershipFormsFooterButtonProps
  extends Pick<
    CheckoutButtonsProps,
    'addToCartProps' | 'buyNowProps' | 'buyNowVisible' | 'addToCartVisible'
  > {}

type MembershipFormsFooterProps = {
  storeItem: StoreItem;
  storeId: string;
  onSuccess?: () => void;
  buttonProps?: MembershipFormsFooterButtonProps;
};

const MembershipFormsFooter = ({
  storeItem,
  storeId,
  onSuccess,
  buttonProps = {},
}: MembershipFormsFooterProps) => {
  const { useGetForms, useScrollToError } = useFormyValidator<MembershipFormsType>();
  const forms = useGetForms();
  const { scrollToError } = useScrollToError({ scrollParams: { offset: 84, duration: 300 } });

  const storeItemId = useMemo(() => storeItem.uuid, [storeItem.uuid]);

  const membershipCartItems = forms.membership_form?.values?.units?.map((unit) => {
    const options = {
      ...unit.customOptions,
      member_type: unit.member_type,
      first_name: unit.first_name,
      last_name: unit.last_name,
      email: unit.email,
      mobile: unit.mobile,
    };
    return {
      store_item_id: storeItem.uuid,
      year: forms.membership_form?.values?.year ? forms.membership_form?.values?.year : null,
      options: options,
    };
  });

  return (
    <CheckoutButtons
      storeId={storeId}
      storeItemId={storeItemId}
      cartItems={membershipCartItems}
      onCartCreate={() => onSuccess?.()}
      addToCartProps={{
        onAddToCartClick: (_args, handleCartUpdate) => {
          handleCartUpdate();
          requestAnimationFrame(() => {
            scrollToError({ scrollTo: 'input', startingFrom: 'top' });
          });
        },
      }}
      buyNowProps={{
        onBuyNowClick: (handleCartUpdate) => {
          handleCartUpdate();
          requestAnimationFrame(() => {
            scrollToError({ scrollTo: 'input', startingFrom: 'top' });
          });
        },
      }}
      validateOnClick
      {...buttonProps}
    />
  );
};

const MembershipItemForm = ({
  allowedYears,
  options,
  units,
  storeItem,
  storeId,
}: MembershipUnitFormsProps) => {
  return (
    <Stack gap="lg">
      {!!storeItem.content.short_description && (
        <Text type="body">{storeItem.content.short_description}</Text>
      )}
      {!!storeItem.content.long_description && (
        <RichTextDisplay html={storeItem.content.long_description} />
      )}

      <MembershipUnitForms options={options} units={units} allowedYears={allowedYears} />
      <MembershipFormsFooter storeId={storeId} storeItem={storeItem} />
    </Stack>
  );
};

export { MembershipFormsFooter, MembershipItemForm, MembershipUnitForms };
